
'use client';

import { useState, useRef, useEffect } from 'react';
import NewsletterModal from './NewsletterModal';

export default function DeskSection() {
  const [showModal, setShowModal] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const renderModal = () => {
    if (!showModal) return null;
    try {
      return (
        <NewsletterModal
          title="MeVER Desk 発売情報"
          description="発売開始時にお知らせいたします"
          formType="desk"
          onClose={() => setShowModal(false)}
        />
      );
    } catch (error) {
      console.error('Failed to render NewsletterModal:', error);
      return null;
    }
  };

  return (
    <>
      <section
        ref={sectionRef}
        id="desk-section"
        className="relative py-20 sm:py-28 md:py-36 bg-[#FAFAF8] overflow-hidden"
        aria-labelledby="desk-heading"
      >
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Right: Image (appears first on mobile) */}
            <div
              className={`relative order-1 lg:order-2 transition-all duration-1000 ${
                isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
              }`}
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-white">
                <img
                  src="https://public.readdy.ai/ai/img_res/50609ab2e8fa0e889d62c9fb17068f69.jpg"
                  alt="MeVER Desk - ミニマルワークスペース"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  width="800"
                  height="1000"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
              </div>

              {/* Floating element */}
              <div
                className={`absolute -top-6 -left-6 bg-neutral-900 text-white rounded-2xl shadow-2xl p-6 transition-all duration-1000 delay-300 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"
                }`}
              >
                <p className="text-xs font-medium tracking-wider uppercase mb-1 text-white/60">
                  Coming
                </p>
                <p className="text-2xl font-extralight tracking-wide">2026.Q2</p>
              </div>
            </div>

            <div className="order-2 md:order-1">
              <h2
                id="desk-heading"
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 sm:mb-8 tracking-tight"
              >
                Stand Up<br />
                Your Mode.
              </h2>
              <p className="text-lg sm:text-xl md:text-2xl font-light text-black/60 mb-8 sm:mb-12 leading-relaxed">
                電動昇降デスク × 抗菌メラミン天板 (Core)。<br />
                座ると立つを、シームレスに。
              </p>
              <ul
                className="space-y-4 sm:space-y-6 mb-8 sm:mb-12"
                aria-label="MeVER Deskの特徴"
              >
                <li className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="w-1.5 h-1.5 bg-[#8E7AB5] rounded-full mt-2.5 sm:mt-3 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-base sm:text-lg font-light text-black/80">
                    電動昇降式 昇降速度40m/s (Pro/Core)
                  </p>
                </li>
                <li className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="w-1.5 h-1.5 bg-[#8E7AB5] rounded-full mt-2.5 sm:mt-3 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-base sm:text-lg font-light text-black/80">
                    最低62cm〜最高125cmのワイドレンジ昇降幅
                  </p>
                </li>
                <li className="flex items-start gap-3 sm:gap-4">
                  <div
                    className="w-1.5 h-1.5 bg-[#8E7AB5] rounded-full mt-2.5 sm:mt-3 flex-shrink-0"
                    aria-hidden="true"
                  />
                  <p className="text-base sm:text-lg font-light text-black/80">
                    日本の職人による製作天板 (Pro/Core)
                  </p>
                </li>
              </ul>
              <button
                onClick={() => setShowModal(true)}
                className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-wide hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
                aria-haspopup="dialog"
              >
                発売に関する情報を受け取る
              </button>
            </div>
          </div>
        </div>
      </section>

      {renderModal()}
    </>
  );
}
